import { useCallback, useEffect, useMemo, useRef, useState, type ReactElement, type ReactNode } from 'react';
import { creerClient, ErreurApi } from '../../api/client.js';
import type { Ecran } from '../../api/contrat.js';
import { cheminEcran } from '../../app/ecrans.js';
import { CIBLE_MIN, COULEURS } from '../../app/styles.js';
import {
  BlocActionUnique,
  BlocEchanges,
  BlocFamille,
  BlocPaiement,
  BlocVideos,
  Bloc,
  FriseHorizontale,
  LienVoirMonArbre,
  ModaleRenonciation,
  stylePrincipal,
  styleRacine,
  styleRangee,
  styleSecondaire,
  styleTexteSecondaire,
  TitreEcran,
  type VideoPortail,
} from './composants.js';
import { BarreOngletsPortail, useEstMobile } from './barre-onglets.js';
import { CANAUX, TEXTES, TEXTES_M07 } from './libelles.js';
import {
  choixConfidentialite,
  corpsModification,
  saisieDepuis,
  type AccueilPortail,
  type CanalChoisi,
  type ConfidentialiteLue,
  type SaisieChoix,
} from './modele.js';
import { creerSource, type EntreeJournal, type GenreDemande, type SourcePortail } from './source.js';

/**
 * Écrans des portails client branchés dans le registre de la coquille (`{ ecran }`) : ils créent le client de l'API, lisent
 * les routes du portail et branchent les actions. Le `fetch` est injectable pour les tests, jamais deviné.
 */
export interface DependancesPortail {
  fetch?: typeof fetch;
}

type Lecture<T> = { statut: 'chargement' } | { statut: 'ok'; donnees: T } | { statut: 'erreur'; message: string };

const messageDe = (e: unknown): string => (e instanceof ErreurApi ? e.messageUtilisateur : TEXTES.erreurInconnue);

function useSource(dependances: DependancesPortail | undefined): SourcePortail {
  const brut = dependances?.fetch;
  return useMemo(() => creerSource(creerClient({ fetch: brut ?? ((...a) => globalThis.fetch(...a)) })), [brut]);
}

function useLecture<T>(charger: () => Promise<T>): { lecture: Lecture<T>; recharger: () => void } {
  const [lecture, setLecture] = useState<Lecture<T>>({ statut: 'chargement' });
  const [version, setVersion] = useState(0);
  useEffect(() => {
    let annule = false;
    setLecture({ statut: 'chargement' });
    charger().then(
      (donnees) => !annule && setLecture({ statut: 'ok', donnees }),
      (e: unknown) => !annule && setLecture({ statut: 'erreur', message: messageDe(e) }),
    );
    return () => {
      annule = true;
    };
    // Relu au montage et à chaque « Réessayer » ; la source est stable pour la vie de l'écran.
  }, [version]);
  return { lecture, recharger: useCallback(() => setVersion((v) => v + 1), []) };
}

const ONGLETS_HERITIER = new Set(['M04', 'X14', 'M07']);

/** Les écrans de l'héritier portent la barre d'onglets du téléphone ; la place du bas est réservée pour qu'elle ne masque rien. */
function Racine({ id, enfants, piece = null }: { id: string; enfants: ReactNode; piece?: string | null }) {
  const mobile = useEstMobile();
  const barre = ONGLETS_HERITIER.has(id);
  return (
    <section data-ecran={id} style={barre && mobile ? { ...styleRacine, paddingBottom: 96 } : styleRacine}>
      {enfants}
      {barre ? <BarreOngletsPortail actif={id} piece={piece} /> : null}
    </section>
  );
}

/** Tant que le dossier n'est pas lu, le niveau 1 est le titre de l'écran au contrat (la coquille l'exige). */
function EtatAttente({
  id,
  titre,
  lecture,
  recharger,
  chargement,
}: {
  id: string;
  titre: string;
  lecture: Lecture<unknown>;
  recharger: () => void;
  chargement: string;
}) {
  return (
    <Racine
      id={id}
      enfants={
        lecture.statut === 'chargement' ? (
          <>
            <TitreEcran titre={titre} />
            <p role="status">{chargement}</p>
          </>
        ) : lecture.statut === 'erreur' ? (
          <>
            <TitreEcran titre={titre} />
            <p role="alert" style={{ color: COULEURS.rouge }}>
              {lecture.message}
            </p>
            <button type="button" onClick={recharger} style={styleSecondaire}>
              {TEXTES.reessayer}
            </button>
          </>
        ) : null
      }
    />
  );
}

/** Lit l'accueil du portail ; rend l'état d'attente (chargement, erreur, dossier vide) ou le contenu. */
function EcranAccueil({
  id,
  titre,
  dependances,
  rendre,
}: {
  id: string;
  titre: string;
  dependances: DependancesPortail | undefined;
  rendre: (accueil: AccueilPortail, source: SourcePortail) => ReactNode;
}): ReactElement {
  const source = useSource(dependances);
  const { lecture, recharger } = useLecture(source.chargerAccueil);
  if (lecture.statut !== 'ok')
    return <EtatAttente id={id} titre={titre} lecture={lecture} recharger={recharger} chargement={TEXTES.chargement} />;
  if (lecture.donnees.etapes.length === 0)
    return (
      <Racine
        id={id}
        enfants={
          <>
            <TitreEcran titre={titre} />
            <p role="status">{TEXTES.dossierVide}</p>
          </>
        }
      />
    );
  return <Racine id={id} piece={lecture.donnees.action.requirementId ?? null} enfants={rendre(lecture.donnees, source)} />;
}

type EtatDemande = { statut: 'repos' | 'envoi' | 'faite' } | { statut: 'erreur'; message: string };

/** L'action unique, avec « demandez-le pour moi » branché sur `portail.pieces.demander` (PER-3-02, PER-8-02). */
function ActionUniqueBranchee({ action, source }: { action: AccueilPortail['action']; source: SourcePortail }) {
  const [demande, setDemande] = useState<EtatDemande>({ statut: 'repos' });
  const requirementId = action.requirementId ?? null;
  const demander = () => {
    if (requirementId === null || demande.statut === 'envoi' || demande.statut === 'faite') return;
    setDemande({ statut: 'envoi' });
    source.demanderPiece(requirementId).then(
      () => setDemande({ statut: 'faite' }),
      (e: unknown) => setDemande({ statut: 'erreur', message: messageDe(e) }),
    );
  };
  return (
    <>
      <BlocActionUnique
        action={demande.statut === 'faite' ? { ...action, libelleAlternative: null } : action}
        {...(requirementId === null ? {} : { onAlternative: demander })}
      />
      {demande.statut === 'faite' ? <p role="status">{TEXTES.demandeFaite}</p> : null}
      {demande.statut === 'erreur' ? (
        <p role="alert" style={{ color: COULEURS.rouge }}>
          {demande.message}
        </p>
      ) : null}
    </>
  );
}

const Parcours = ({ accueil, arbre }: { accueil: AccueilPortail; arbre: boolean }) => (
  <Bloc
    titre={TEXTES.parcours}
    enfants={
      <>
        <FriseHorizontale etapes={accueil.etapes} />
        {arbre ? <LienVoirMonArbre /> : null}
      </>
    }
  />
);

/** M04 · Empreinte de progression et action unique (héritier). */
export function EcranM04({ ecran, dependances }: { ecran: Ecran; dependances?: DependancesPortail }): ReactElement {
  return (
    <EcranAccueil
      id="M04"
      titre={ecran.titre}
      dependances={dependances}
      rendre={(a, source) => (
        <>
          <TitreEcran titre={a.titreDossier} salutation={a.salutation} />
          <Parcours accueil={a} arbre />
          <ActionUniqueBranchee action={a.action} source={source} />
        </>
      )}
    />
  );
}

/** Vidéos partagées par le cabinet ; en panne ou vide, rien n'est montré (elles ne bloquent jamais le portail). */
function VideosPartagees({ source }: { source: SourcePortail }) {
  const { lecture } = useLecture<VideoPortail[]>(source.chargerVideos);
  return lecture.statut === 'ok' ? <BlocVideos videos={lecture.donnees} /> : null;
}

function ContenuX13({ accueil, source }: { accueil: AccueilPortail; source: SourcePortail }) {
  const { lecture } = useLecture<EntreeJournal[]>(source.chargerJournal);
  return (
    <>
      <TitreEcran titre={accueil.titreDossier} salutation={accueil.salutation} />
      <Parcours accueil={accueil} arbre />
      <BlocPaiement paiement={accueil.paiement} />
      <VideosPartagees source={source} />
      <BlocEchanges
        etat={
          lecture.statut === 'ok'
            ? { statut: 'ok', entrees: lecture.donnees }
            : lecture.statut === 'erreur'
              ? { statut: 'erreur' }
              : { statut: 'chargement' }
        }
      />
    </>
  );
}

/** X13 · Portail du mandant : parcours, participation à l'accompagnement du cabinet, échanges. Aucune demande visant une personne. */
export function EcranX13({ ecran, dependances }: { ecran: Ecran; dependances?: DependancesPortail }): ReactElement {
  return (
    <EcranAccueil
      id="X13"
      titre={ecran.titre}
      dependances={dependances}
      rendre={(a, source) => <ContenuX13 accueil={a} source={source} />}
    />
  );
}

function ContenuX14({ accueil, source }: { accueil: AccueilPortail; source: SourcePortail }) {
  const [renonciation, setRenonciation] = useState(false);
  return (
    <>
      <TitreEcran titre={accueil.titreDossier} salutation={accueil.salutation} />
      <Parcours accueil={accueil} arbre={false} />
      <ActionUniqueBranchee action={accueil.action} source={source} />
      <BlocFamille famille={accueil.famille} actions={<LienVoirMonArbre />} />
      <VideosPartagees source={source} />
      <Bloc
        titre={TEXTES.choix}
        enfants={
          <div style={styleRangee}>
            <a href={cheminEcran('M07')} style={styleSecondaire}>
              {TEXTES.gererChoix}
            </a>
            <button type="button" onClick={() => setRenonciation(true)} style={styleSecondaire}>
              {TEXTES.renonciationBouton}
            </button>
          </div>
        }
      />
      {renonciation ? <ModaleRenonciation onFermer={() => setRenonciation(false)} /> : null}
    </>
  );
}

/** X14 · Portail de l'héritier : sa branche seulement, une seule action, ses choix. */
export function EcranX14({ ecran, dependances }: { ecran: Ecran; dependances?: DependancesPortail }): ReactElement {
  return (
    <EcranAccueil
      id="X14"
      titre={ecran.titre}
      dependances={dependances}
      rendre={(a, source) => <ContenuX14 accueil={a} source={source} />}
    />
  );
}

const styleChamp: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  minHeight: CIBLE_MIN,
  boxSizing: 'border-box',
  cursor: 'pointer',
};

function FormulaireConfidentialite({ source, initial }: { source: SourcePortail; initial: ConfidentialiteLue }) {
  const [lu, setLu] = useState(initial);
  const [saisie, setSaisie] = useState<SaisieChoix>(() => saisieDepuis(initial));
  const [annonce, setAnnonce] = useState<{ type: 'status' | 'alert'; texte: string } | null>(null);
  const [enCours, setEnCours] = useState(false);
  const [confirmation, setConfirmation] = useState(false);
  const verrou = useRef(false);

  const corps = corpsModification(lu, saisie);

  const enregistrer = async () => {
    if (!corps || verrou.current) return;
    verrou.current = true;
    setEnCours(true);
    setAnnonce(null);
    try {
      const nouveau = await source.enregistrerChoix(corps);
      setLu(nouveau);
      setSaisie(saisieDepuis(nouveau));
      setAnnonce({ type: 'status', texte: TEXTES_M07.enregistres });
    } catch (e) {
      setAnnonce({ type: 'alert', texte: messageDe(e) });
    } finally {
      verrou.current = false;
      setEnCours(false);
    }
  };

  const demander = async (genre: GenreDemande) => {
    if (verrou.current) return;
    verrou.current = true;
    setAnnonce(null);
    try {
      await source.demanderDonnees(genre);
      setConfirmation(false);
      setAnnonce({ type: 'status', texte: TEXTES_M07.demandeEnregistree });
    } catch (e) {
      setAnnonce({ type: 'alert', texte: messageDe(e) });
    } finally {
      verrou.current = false;
    }
  };

  return (
    <>
      <p>{TEXTES_M07.introduction}</p>
      <Bloc
        titre={TEXTES.choix}
        enfants={
          <>
            <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
              {choixConfidentialite(lu).map((c) => (
                <li key={c.finalite} style={{ paddingBlock: 4 }}>
                  <label style={styleChamp}>
                    <input
                      type="checkbox"
                      checked={saisie.finalites[c.finalite]}
                      onChange={(e) => setSaisie({ ...saisie, finalites: { ...saisie.finalites, [c.finalite]: e.target.checked } })}
                      style={{ width: 24, height: 24 }}
                    />
                    {c.libelle}
                  </label>
                  <small style={{ ...styleTexteSecondaire, fontSize: 18, display: 'block' }}>
                    {c.le ? `Choix du ${c.le}` : 'Pas encore de choix enregistré'}
                  </small>
                </li>
              ))}
            </ul>
            <div style={{ marginBlock: 12 }}>
              <label htmlFor="canal-portail" style={{ ...styleChamp, cursor: 'default' }}>
                {TEXTES_M07.canal}
              </label>
              <select
                id="canal-portail"
                value={saisie.canal ?? ''}
                onChange={(e) => setSaisie({ ...saisie, canal: (e.target.value || null) as CanalChoisi | null })}
                style={{ minHeight: CIBLE_MIN, font: 'inherit', padding: '0 8px', maxWidth: '100%' }}
              >
                <option value="">Aucune préférence</option>
                {CANAUX.map((c) => (
                  <option key={c.canal} value={c.canal}>
                    {c.libelle}
                  </option>
                ))}
              </select>
            </div>
            <button
              type="button"
              disabled={!corps || enCours}
              onClick={() => void enregistrer()}
              style={{ ...stylePrincipal, opacity: !corps || enCours ? 0.6 : 1 }}
            >
              {TEXTES_M07.enregistrer}
            </button>
            <p style={styleTexteSecondaire}>{TEXTES_M07.datation}</p>
          </>
        }
      />
      <Bloc
        titre={TEXTES_M07.quiVoit}
        enfants={
          <>
            <p>{TEXTES_M07.quiVoitTexte}</p>
            <div style={styleRangee}>
              <button type="button" onClick={() => void demander('acces')} style={styleSecondaire}>
                {TEXTES_M07.copie}
              </button>
              {confirmation ? null : (
                <button type="button" onClick={() => setConfirmation(true)} style={styleSecondaire}>
                  {TEXTES_M07.suppression}
                </button>
              )}
            </div>
            {confirmation ? (
              <div style={styleRangee}>
                <p style={{ flexBasis: '100%', margin: 0 }}>{TEXTES_M07.confirmationSuppression}</p>
                <button type="button" onClick={() => void demander('suppression')} style={stylePrincipal}>
                  {TEXTES_M07.confirmerSuppression}
                </button>
                <button type="button" onClick={() => setConfirmation(false)} style={styleSecondaire}>
                  {TEXTES_M07.annuler}
                </button>
              </div>
            ) : null}
          </>
        }
      />
      {annonce ? (
        <p role={annonce.type} style={annonce.type === 'alert' ? { color: COULEURS.rouge } : undefined}>
          {annonce.texte}
        </p>
      ) : null}
    </>
  );
}

/** M07 · Mes données et ma confidentialité : consentements détaillés, canal, demandes RGPD. */
export function EcranM07({ ecran, dependances }: { ecran: Ecran; dependances?: DependancesPortail }): ReactElement {
  const source = useSource(dependances);
  const { lecture, recharger } = useLecture(source.chargerConfidentialite);
  if (lecture.statut !== 'ok')
    return (
      <Racine
        id="M07"
        enfants={
          <>
            <TitreEcran titre={ecran.titre} />
            {lecture.statut === 'chargement' ? (
              <p role="status">{TEXTES.chargementChoix}</p>
            ) : (
              <>
                <p role="alert" style={{ color: COULEURS.rouge }}>
                  {lecture.message}
                </p>
                <button type="button" onClick={recharger} style={styleSecondaire}>
                  {TEXTES.reessayer}
                </button>
              </>
            )}
          </>
        }
      />
    );
  return (
    <Racine
      id="M07"
      enfants={
        <>
          <TitreEcran titre={ecran.titre} />
          <FormulaireConfidentialite source={source} initial={lecture.donnees} />
        </>
      }
    />
  );
}
